import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import FlashSaleBanner from "./FlashSaleBanner";

export default function CartDrawer() {
  const { items, remove, updateQty, subtotal, isOpen, setIsOpen, clear } = useCart();

  const fakeCheckout = () => {
    toast.success("Checkout coming soon — your selection has been reserved.");
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[95] w-full sm:w-[440px] bg-ink-900 border-l border-white/10 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
              <div>
                <div className="label-eyebrow">Your Selection</div>
                <h3 className="font-serif text-2xl mt-1">Cart</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-mist hover:text-gold"
                aria-label="Close cart"
                data-testid="close-cart-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar px-8 py-6 space-y-6">
              {items.length === 0 && (
                <div className="text-center py-20" data-testid="cart-empty-state">
                  <p className="font-serif text-2xl text-bone mb-3">Your cart is empty</p>
                  <p className="text-mist text-sm mb-8 font-light">Discover the collection.</p>
                  <Link to="/shop" onClick={() => setIsOpen(false)} className="btn-ghost inline-flex">
                    Browse shop
                  </Link>
                </div>
              )}
              {items.map((it) => (
                <div key={it.id} className="flex gap-4 border-b border-white/5 pb-6" data-testid={`cart-item-${it.id}`}>
                  <div className="w-20 h-24 product-image-wrap shrink-0">
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <p className="font-serif text-base text-bone leading-tight">{it.name}</p>
                    <p className="text-gold text-sm mt-1">₹{it.price}</p>
                    <div className="flex items-center mt-3 gap-3">
                      <button
                        onClick={() => updateQty(it.id, it.qty - 1)}
                        className="w-7 h-7 border border-white/15 text-bone hover:border-gold hover:text-gold flex items-center justify-center"
                        data-testid={`cart-qty-decrease-${it.id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm font-light text-bone w-6 text-center">{it.qty}</span>
                      <button
                        onClick={() => updateQty(it.id, it.qty + 1)}
                        className="w-7 h-7 border border-white/15 text-bone hover:border-gold hover:text-gold flex items-center justify-center"
                        data-testid={`cart-qty-increase-${it.id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => remove(it.id)}
                        className="ml-auto text-mist hover:text-burgundy"
                        data-testid={`cart-remove-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10">
                <FlashSaleBanner variant="slim" />
                <div className="px-8 py-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="label-eyebrow text-mist">Subtotal</span>
                    <span className="num-display text-2xl text-gold" data-testid="cart-subtotal">₹{subtotal}</span>
                  </div>
                  <p className="text-mist text-xs font-light">Discreet shipping calculated at checkout.</p>
                  <button onClick={fakeCheckout} className="btn-primary w-full" data-testid="cart-checkout-btn">
                    Reserve Checkout
                  </button>
                  <button onClick={clear} className="w-full text-xs tracking-[0.2em] uppercase text-mist hover:text-burgundy">
                    Empty cart
                  </button>
                </div>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
